import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import confetti from 'canvas-confetti';

import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { usePrintCreditsBalance } from 'modules/v2/common/hooks/usePrintCreditsBalance';
import {
  getRouteMailOnDemandCredits,
  getRouteMailOnDemandOrder,
  getRouteMailOnDemandOrders,
} from 'modules/v2/routes/navigation';

import CopyableOrderId from '../../components/CopyableOrderId';
import { CoverLetterChip } from '../../components/CoverLetterChip';
import CoverLetterMiniPreview from '../../components/CoverLetterMiniPreview';
import strings, { formatMessage } from '../../strings';
import { formatCredits } from '../../utils';

/** Honour the user's OS "reduce motion" setting before firing the confetti. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ConfirmationStep = ({ order }) => {
  const history = useHistory();
  const { data: balanceData } = usePrintCreditsBalance();
  const celebrated = useRef(false);
  // Fall back to the kind placeholder if the thumbnail URL is present but 404s.
  const [thumbnailBroken, setThumbnailBroken] = useState(false);

  // One tasteful confetti burst on mount; skipped entirely for users who
  // prefer reduced motion. The ref guards against a double-fire under React
  // StrictMode's intentional double-invoke.
  useEffect(() => {
    if (celebrated.current || prefersReducedMotion()) {
      return;
    }
    celebrated.current = true;
    confetti({
      particleCount: 90,
      spread: 70,
      startVelocity: 32,
      origin: { y: 0.35 },
      disableForReducedMotion: true,
    });
  }, []);

  const asset = order?.asset || {};
  const pricing = order?.pricing || {};
  const resolution = order?.resolution || {};
  const recipients =
    resolution.selectedMailableCount ?? resolution.mailableCount ?? 0;
  const previewUrl = asset.previewPageUrl ?? asset.previewUrl;
  const isBook = asset.kind === 'book';

  const summaryRows = [
    {
      label: strings.pay.confirmationRecipientsLabel,
      value: formatCredits(recipients),
    },
    {
      label: strings.pay.confirmationChargedLabel,
      value: formatCredits(pricing.creditsRequired),
    },
  ];

  if (typeof balanceData?.balance === 'number') {
    summaryRows.push({
      label: strings.pay.confirmationRemainingLabel,
      value: formatCredits(balanceData.balance),
      testId: 'mod-confirmation-remaining',
    });
  }

  return (
    <div
      className="mx-auto flex max-w-xl flex-col items-center gap-4 py-12 text-center"
      data-testid="mod-confirmation"
    >
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl font-bold text-green-600"
      >
        ✓
      </div>
      <div>
        <h2 className="text-2xl font-semibold text-neutral-800">
          {strings.pay.confirmationTitle}
        </h2>
        <p className="mt-1 text-neutral-600">{strings.pay.confirmationBody}</p>
      </div>

      <div className="w-full rounded-lg border border-neutral-200 bg-white p-4 text-left">
        <div className="flex items-start gap-4">
          <div className="flex shrink-0 flex-col items-center gap-2">
            {asset.thumbnailUrl && !thumbnailBroken ? (
              <img
                src={asset.thumbnailUrl}
                alt={formatMessage(strings.pay.confirmationAssetAlt, {
                  title: asset.title || '',
                })}
                className="h-20 w-16 shrink-0 rounded border border-neutral-200 object-cover"
                loading="lazy"
                onError={() => setThumbnailBroken(true)}
                data-testid="mod-confirmation-thumbnail"
              />
            ) : (
              <div className="flex h-20 w-16 shrink-0 items-center justify-center rounded border border-neutral-200 bg-neutral-50 px-1 text-center text-[10px] font-semibold uppercase text-neutral-400">
                {isBook ? strings.assets.kindBook : strings.assets.kindTbExport}
              </div>
            )}
            {order?.coverLetter && (
              <CoverLetterMiniPreview
                templateKey={order.coverLetter.templateKey}
                body={order.coverLetter.editedBody}
                width={64}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold text-neutral-800">
                {asset.title}
              </span>
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-sm font-semibold text-primary-700 underline"
                  data-testid="mod-confirmation-preview"
                >
                  {strings.pay.confirmationPreview}
                </a>
              )}
            </div>
            <table className="mt-2 w-full text-sm">
              <tbody>
                {summaryRows
                  .filter((row) => row.value !== undefined && row.value !== null)
                  .map((row) => (
                    <tr
                      key={row.label}
                      className="border-b border-neutral-100 last:border-0"
                    >
                      <td className="py-1.5 text-neutral-500">{row.label}</td>
                      <td
                        className="py-1.5 text-right font-medium"
                        data-testid={row.testId}
                      >
                        {row.value}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {order?.coverLetter && (
              <div className="mt-2" data-testid="mod-confirmation-cover-letter">
                <CoverLetterChip templateKey={order.coverLetter.templateKey} />
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex justify-center border-t border-neutral-100 pt-3">
          <CopyableOrderId orderId={order?._id} />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
        <Button
          type="primary"
          data-testid="mod-view-order-link"
          onClick={() => history.push(getRouteMailOnDemandOrder(order?._id))}
        >
          {strings.pay.viewOrder}
        </Button>
        <Button
          type="outlined"
          data-testid="mod-view-orders-link"
          onClick={() => history.push(getRouteMailOnDemandOrders())}
        >
          {strings.pay.backToOrders}
        </Button>
        <button
          type="button"
          data-testid="mod-view-credits-link"
          onClick={() => history.push(getRouteMailOnDemandCredits())}
          className="text-sm font-semibold text-primary-700 underline"
        >
          {strings.pay.viewCreditsHistory}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationStep;
